import React from 'react'
import { useState, useEffect, useRef, useMemo } from 'react'
import { useNavigate, useOutletContext, useParams } from 'react-router-dom'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { COUNTDOWN_PLACEHOLDER, useCountdown } from '@renderer/utils/countdownUtils'
import {
  isFramedStripRecording,
  isRecordingPackageType,
  toRecordingType
} from '@renderer/utils/recordingType'
import cogoToast from '@successtar/cogo-toast'
import { useConfigStore } from '@renderer/stores/configState'
import { FrameOption, FrameConfig } from '@renderer/types/configState'
import PrintPreview, { PrintPreviewRef, ExportResult } from '@renderer/components/PrintPreview'
import { Button, ProcessingModal } from '@renderer/components'
import PhotoFrame from '@renderer/components/PhotoFrame'
import { FaExchangeAlt } from 'react-icons/fa'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { backgroundVideoProcessor } from '@renderer/utils/backgroundVideoProcessor'
import { usePrintStore } from '@renderer/stores/printStore'
import { useTranslation } from 'react-i18next'
import { useSharedCountdown } from './hooks/useSharedCountdown'
import { printSessionStrip } from '@renderer/utils/pipeline/sessionPrint'
import {
  processAndUploadStrip,
  processAndUploadGIF,
  processAndUploadPhotos,
  stateUploader
} from '@renderer/utils/pipeline/outputOrchestrator'
import type { ProcessingContext } from '@renderer/types/outputPipeline.types'
import type { CompositionManifest } from '@renderer/types/sessionState'
import { BackgroundContainer, GlassPanel } from '@renderer/components/BackgroundContainer'
import { useBackgroundTextColor } from '@renderer/hooks/useBackgroundTextColor'
import { useDesignLayout, useSessionLayout } from '@renderer/hooks'
import type { PhotoState } from '@renderer/components/PrintPreview'
import { waitPrintResultMinDisplay } from '@renderer/utils/printResultMinDisplay'
import { resolveFramedVideoPlaybackAndRemote } from '@renderer/utils/galleryUrlUtils'
import { buildPhotoTypeToSelectedIndex } from '@renderer/utils/frameUtils'
import { navigateAfterOutputsReady } from '@renderer/utils/navigateAfterOutputs'
import { backgroundFilterProcessor } from '@renderer/utils/backgroundFilterProcessor'
import { filteredUrlsForSelection } from '@renderer/utils/filterLeftovers'
import { waitForValue } from '@renderer/utils/waitForValue'

/**
 * Photos the post-capture Filter page ran out of time for keep filtering in the background
 * (utils/filterLeftovers). The GIF, the photo upload and the strip export each hold for them
 * at most this long; past it they go out with what is filtered, as before. One cap for all:
 * the export's wait never starts before the media's, so the uploads always start no later
 * than the export, ahead of Edit and of the session ending.
 */
const LEFTOVER_FILTER_WAIT_MS = 30000
/** Once the leftovers land, how long to wait for the strip preview to load them. */
const LEFTOVER_PREVIEW_SWAP_WAIT_MS = 8000

interface UserContext {
  user?: {
    email: string
  }
}

const SelectPage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { projectId } = useParams()
  const context = useOutletContext<UserContext | null>()
  const {
    gallery,
    galleryKey,
    setSessionState,
    userPreferences,
    id,
    logActivity
  } = useSessionStateStore()
  const { getFrame, config: globalConfig, frames, getPageSettings } = useConfigStore()
  const config = globalConfig?.pages.select
  const selectionTimeoutSec = config?.settings?.selectionTimeout ?? 0
  const captureConfig = globalConfig?.pages.capture
  const captureMode = captureConfig?.settings.mode || 'standard'
  const { primaryColor, secondaryColor } = globalConfig?.theme || {
    primaryColor: '#3B82F6',
    secondaryColor: '#FFFFFF'
  }
  const [selectedPhotos, setSelectedPhotos] = useState<string[]>([])

  // Helper function to resolve photoUrl to filtered version if available
  // This ensures PrintPreview and strip generation use filtered photos even if photoUrl wasn't updated
  // Handles cases where:
  // 1. applyAfterCapture=true: photoUrl is unfiltered but filteredVersions exists (Filter.tsx failed to update photoUrl)
  // 2. applyAfterCapture=false: photoUrl is already filtered (filter applied during capture)
  // 3. applyAfterCapture=true: photoUrl is already filtered (Filter.tsx succeeded)
  const resolvePhotoUrl = (photoUrl: string): string => {
    const selectedFilterId = userPreferences.filterId
    if (!selectedFilterId) return photoUrl

    // Try to find item by photoUrl (works for both filtered and unfiltered URLs)
    const item = gallery.capturedItems.find(
      (item) => item.photoUrl === photoUrl || item.originalPhotoUrl === photoUrl
    )
    
    // Also check if photoUrl matches any filteredVersions (in case photoUrl is already filtered)
    if (!item) {
      const itemByFilteredVersion = gallery.capturedItems.find((item) =>
        Object.values(item.filteredVersions || {}).includes(photoUrl)
      )
      // Only use filteredVersions if it exists for the CURRENT selected filter
      // This prevents using wrong filter version when applyAfterCapture=false
      if (itemByFilteredVersion?.filteredVersions?.[selectedFilterId]) {
        return itemByFilteredVersion.filteredVersions[selectedFilterId]
      }
      // If photoUrl matches a filteredVersion but not for current filter, return as-is
      // (photoUrl is already filtered with current filter when applyAfterCapture=false)
      return photoUrl
    }

    // If filtered version exists for selected filter, use it
    // This handles applyAfterCapture=true case where Filter.tsx created filteredVersions
    if (item.filteredVersions?.[selectedFilterId]) {
      return item.filteredVersions[selectedFilterId]
    }
    
    // Otherwise return the photoUrl as-is
    // When applyAfterCapture=false: photoUrl is already filtered (applied during capture)
    // When applyAfterCapture=true: photoUrl might be unfiltered (Filter.tsx failed) or already filtered
    return photoUrl
  }
  const [remainingTime, setRemainingTime] = useState<string>(COUNTDOWN_PLACEHOLDER)
  const [isUploading, setIsUploading] = useState(false)
  const previewRef = useRef<PrintPreviewRef>(null)
  const [mirroredPhotos, setMirroredPhotos] = useState<Set<string>>(new Set())
  const [isAllMirrored, setIsAllMirrored] = useState(false)
  const [isSelectionLocked, setIsSelectionLocked] = useState(false)
  const { downloadLocal } = useSettingsStore()
  const [videoProcessingId, setVideoProcessingId] = useState<string | null>(null)
  const { getPrinterForFrame } = usePrintStore()
  const uploadStartedRef = useRef<boolean>(false)
  const printJobSentRef = useRef<boolean>(false)
  const isOnlineRef = useRef<boolean>(true)
  const isMountedRef = useRef(true)
  const isProcessingRef = useRef(false)
  const navigationLockRef = useRef(false)
  const videoProcessingIdRef = useRef<string | null>(null)
  // The selection countdown is armed once, on mount. Its callback goes through
  // this ref so it always runs the current handleCountdownComplete, never the
  // mount-time closure (see the assignment below the handler).
  const handleCountdownCompleteRef = useRef<(() => void) | null>(null)
  const textColor = useBackgroundTextColor()
  const selectTimerUsesDarkChrome =
    textColor.replace(/\s/g, '').toLowerCase() === '#ffffff' ||
    textColor.replace(/\s/g, '').toLowerCase() === '#fff'

  // Simple loading state management
  const [isPreviewReady, setIsPreviewReady] = useState(false)

  // Initialize countdown hook
  const selectionCountdown = useCountdown(config?.settings?.selectionTimeout || 0)
  
  // Use shared countdown for consistent UI
  const {
    remainingTime: sharedRemainingTime,
    isExpired: isSharedCountdownExpired
  } = useSharedCountdown()
  
  const {
    designW: screenWidth,
    designH: screenHeight,
    isPortrait: isPotrait,
    isCompact,
    isTiny
  } = useDesignLayout()
  const { isCompact: viewportCompact, isLandscapeViewport } = useSessionLayout()
  const selectMobileLandscape = viewportCompact && isLandscapeViewport
  const stackSelectTop = isCompact && isPotrait
  /** Floating Next: compact width (no oversized min-w on mobile vs desktop strip button) */
  const selectNextStackedBtn =
    isCompact && selectMobileLandscape
      ? '!inline-flex !h-9 !min-h-9 !w-auto !max-w-[9.5rem] !shrink-0 !items-center !justify-center !px-3 !py-1.5 !text-xs !leading-snug'
      : isCompact
        ? '!inline-flex !h-10 !min-h-10 !w-auto !max-w-[11rem] !shrink-0 !items-center !justify-center !px-4 !py-2 !text-sm !leading-snug'
        : '!inline-flex !min-h-[3.25rem] !w-auto !max-w-[14rem] !items-center !justify-center !px-6 !py-3 !text-lg !leading-snug'
  /** Compact design landscape: tighter chrome + preview fits canvas without overflow */
  const selectLandscapeCompact = isCompact && !isPotrait
  /** Portrait, non-compact: thumbnails left + glass strip preview right (e.g. 1080×1920) */
  const selectPortraitSideBySide = isPotrait && !stackSelectTop && !selectLandscapeCompact
  /** Print preview fits entirely in the pane (no clipping); same idea as object-contain */
  const selectPreviewAspectBox =
    stackSelectTop ||
    selectLandscapeCompact ||
    selectPortraitSideBySide ||
    (!stackSelectTop && isLandscapeViewport)
  /** Same breakpoint and type scale as FrameBasedCapture / Retake */
  const frameSessionTimerCompact = isCompact || isTiny
  const sessionCountdownTop = Math.max(screenHeight * 0.02, 32)
  const sessionCountdownRight = Math.max(screenWidth * 0.02, 32)
  const sessionCountdownFontSize = Math.min(Math.max(screenHeight * 0.022, 22), 34)

  const renderSharedSessionTimer = (): React.ReactElement => (
    <div
      className={`absolute z-50 ${frameSessionTimerCompact ? 'right-3 top-3 sm:right-4 sm:top-4' : ''}`}
      style={
        frameSessionTimerCompact
          ? undefined
          : { top: `${sessionCountdownTop}px`, right: `${sessionCountdownRight}px` }
      }
    >
      {selectTimerUsesDarkChrome ? (
        frameSessionTimerCompact ? (
          <div className="rounded-lg border border-white/35 bg-black/60 px-2.5 pt-1 pb-1.5 text-center shadow-lg backdrop-blur-sm">
            <div className="text-[10px] font-medium uppercase tracking-wide text-white/75">
              {t('capture.timeRemaining', 'Time remaining')}
            </div>
            <div className="font-mono text-base font-semibold tabular-nums leading-none text-white drop-shadow-md">
              {sharedRemainingTime}
            </div>
          </div>
        ) : (
          <div className="rounded-lg border-2 border-white/90 bg-black/40 p-3 text-center shadow-lg backdrop-blur-sm sm:p-4">
            <div className="mb-1 text-sm text-white/90">
              {t('capture.timeRemaining', 'Time remaining')}
            </div>
            <div
              className="font-bold font-mono tabular-nums text-white"
              style={{ fontSize: `${sessionCountdownFontSize}px` }}
            >
              {sharedRemainingTime}
            </div>
          </div>
        )
      ) : frameSessionTimerCompact ? (
        <div
          className="rounded-lg border bg-white/85 px-2.5 pt-1 pb-1.5 text-center shadow-lg backdrop-blur-sm"
          style={{ borderColor: textColor }}
        >
          <div
            className="text-[10px] font-medium uppercase tracking-wide opacity-75"
            style={{ color: textColor }}
          >
            {t('capture.timeRemaining', 'Time remaining')}
          </div>
          <div
            className="font-mono text-base font-semibold tabular-nums leading-none"
            style={{ color: textColor }}
          >
            {sharedRemainingTime}
          </div>
        </div>
      ) : (
        <div
          className="rounded-lg border-2 bg-white/80 p-3 text-center shadow-lg backdrop-blur-sm sm:p-4"
          style={{ borderColor: textColor }}
        >
          <div className="mb-1 text-sm opacity-90" style={{ color: textColor }}>
            {t('capture.timeRemaining', 'Time remaining')}
          </div>
          <div
            className="font-mono font-bold tabular-nums"
            style={{ fontSize: `${sessionCountdownFontSize}px`, color: textColor }}
          >
            {sharedRemainingTime}
          </div>
        </div>
      )}
    </div>
  )

  const renderSharedSessionTimerBelowStrip = (): React.ReactElement => (
    <span
      className={`self-center font-mono tabular-nums ${
        selectTimerUsesDarkChrome ? 'text-white' : 'text-carbon'
      } ${isCompact ? 'text-lg' : 'text-3xl'}`}
    >
      {sharedRemainingTime}
    </span>
  )

  const getFrameConfig = (frame: FrameOption | null): FrameConfig => {
    if (!frame)
      return {
        id: '',
        baseImage: '',
        drawParams: {
          canvasWidth: 1200,
          canvasHeight: 1800,
          backgroundColor: '#FFFFFF',
          images: []
        }
      }
    return {
      id: frame._id,
      baseImage: frame.thumbnail,
      drawParams: frame.drawParams
    }
  }

  const { settings: currentPrinterSettings } = getPrinterForFrame(userPreferences.frameId || '')
  const printResolutionScale = currentPrinterSettings.printResolutionScale || 1

  /** Single source of truth for template id (matches Frame.tsx persistence and fallbacks) */
  const preferredFrameId = userPreferences.frameId || frames[0]?._id || ''

  // Frame-based mode: resolve template even when session frameId is missing or stale (e.g. skipped Frame page)
  const selectedFrame =
    captureMode === 'frame-based'
      ? getFrame(preferredFrameId) ?? (frames[0] ? getFrame(frames[0]._id) : null)
      : null

  // Persist a valid frame id when session has none or a deleted id (paths that skip Frame.tsx Next)
  useEffect(() => {
    if (captureMode !== 'frame-based') return
    const first = frames[0]
    if (!first) return
    const currentId = userPreferences.frameId
    if (currentId && getFrame(currentId)) return
    setSessionState({ userPreferences: { frameId: first._id } })
  }, [captureMode, frames, userPreferences.frameId, getFrame, setSessionState])

  // IMPORTANT: Video processing must be isolated from print resolution scale
  // Print resolution scale can be 0.5x, 1x, 2x, 3x, etc., which would create
  // videos that are too large for codec limits (e.g., 2480x3508 * 3x = 7440x10524).
  // Video generation uses automatic resizing to cap at 1920px per dimension,
  // but we prevent unnecessary processing by using unscaled frames.

  // Memoize scaled/unscaled frames + FrameConfig so references stay stable across renders.
  // getFrame(scale!==1) and getFrameConfig() otherwise allocate new objects every render, which
  // retriggers the preview wait effect every frame and can leave PrintPreview blank or stuck.
  const selectedScaledFrame = useMemo((): FrameOption | null => {
    if (!preferredFrameId) {
      return frames[0] ? getFrame(frames[0]._id, printResolutionScale) ?? null : null
    }
    return (
      getFrame(preferredFrameId, printResolutionScale) ??
      (frames[0] ? getFrame(frames[0]._id, printResolutionScale) ?? null : null)
    )
  }, [preferredFrameId, printResolutionScale, frames, getFrame])

  const selectedUnscaledFrame = useMemo((): FrameOption | null => {
    if (!preferredFrameId) {
      return frames[0] ? getFrame(frames[0]._id, 1) ?? null : null
    }
    return (
      getFrame(preferredFrameId, 1) ?? (frames[0] ? getFrame(frames[0]._id, 1) ?? null : null)
    )
  }, [preferredFrameId, frames, getFrame])

  const frameConfig = useMemo(
    (): FrameConfig => getFrameConfig(selectedScaledFrame),
    [selectedScaledFrame]
  )

  const unscaledFrameConfig = useMemo(
    (): FrameConfig => getFrameConfig(selectedUnscaledFrame),
    [selectedUnscaledFrame]
  )
  const hasValidFrameConfig = frameConfig.drawParams.images.length > 0

  // Get unique photo types from frame configuration
  const uniquePhotoTypes = frameConfig.drawParams.images
    .filter((img) => img.type.startsWith('photo-'))
    .map((img) => img.type)
    .filter((value, index, self) => self.indexOf(value) === index)

  const maxSelectablePhotos = uniquePhotoTypes?.length || 0

  const frameAspectRatio =
    frameConfig.drawParams.canvasWidth && frameConfig.drawParams.canvasHeight
      ? frameConfig.drawParams.canvasWidth / frameConfig.drawParams.canvasHeight
      : 1
  const isTallFrame = frameAspectRatio < 0.5

  // FIX 1: Make handleCountdownComplete use a callback to ensure selectedPhotos is current
  const handleCountdownComplete = (): void => {
    if (!isMountedRef.current) return
    
    logActivity('Selection timed out, proceeding with selected photos.')
    setIsSelectionLocked(true)
    setSelectedPhotos((prevSelected: string[]) => {
      let finalSelection = prevSelected

      if (prevSelected.length < maxSelectablePhotos) {
        const remainingNeeded = maxSelectablePhotos - prevSelected.length
        const unselectedPhotos = gallery.capturedItems
          .filter((item) => !prevSelected.includes(item.photoUrl))
          .slice(0, remainingNeeded)
        const autoSelected = unselectedPhotos.map((item) => item.photoUrl)
        finalSelection = [...prevSelected, ...autoSelected]
      }

      // Use shorter timeout and check mount before navigation
      setTimeout(() => {
        if (isMountedRef.current) {
          handleContinueWithPhotos(finalSelection)
        }
      }, 500)

      return finalSelection
    })
  }

  // The mount-time handler still holds the first render's userPreferences, where
  // copies is 0 on a project with Checkout off (the effect that defaults it to 1
  // runs after mount). A guest who tapped Next was printed; one who let the
  // selection timer run out was silently not (B33).
  handleCountdownCompleteRef.current = handleCountdownComplete

  const handleContinueWithPhotos = async (photosToProcess: string[]): Promise<void> => {
    // Check processing lock
    if (isProcessingRef.current) {
      console.warn('[Select] Already processing, ignoring duplicate request')
      return
    }
    
    if (!isMountedRef.current) return
    
    isProcessingRef.current = true
    selectionCountdown.stop()
    setIsUploading(true)
    setIsSelectionLocked(true)
    const processingStartedAt = Date.now()
    let didAttemptPrint = false

    try {
      // Check if Edit page is enabled - use both methods for reliability
      const editSettings = useConfigStore.getState().getPageSettings('edit')
      const editEnabled =
        editSettings?.enabled ?? globalConfig?.pages.edit?.settings?.enabled ?? false

      // Validate frame config before export (getFrame may return undefined if frame was deleted)
      if (!hasValidFrameConfig) {
        logActivity('Strip export aborted: no valid frame config (frame may have been removed)')
        cogoToast.error(t('select.stripExportFailed', 'Failed to create strip preview') + ' — no frame template.')
        if (isMountedRef.current) {
          setIsUploading(false)
          setIsSelectionLocked(false)
        }
        isProcessingRef.current = false
        return
      }
      // Generate photo strip - exportImage() handles waitForReady internally
      if (!previewRef.current) {
        throw new Error(t('select.stripExportFailed', 'Failed to create strip preview'))
      }

      // Slow booths only: photos the Filter page ran out of time for are still being
      // filtered. Hold the export until they land and the preview shows them. Without
      // leftovers nothing waits and `outputGallery` is this render's gallery, as before.
      let outputGallery = gallery
      if (backgroundFilterProcessor.hasLeftovers(id)) {
        const waitStartedAt = Date.now()
        const leftoverWait = await backgroundFilterProcessor.waitForLeftovers(
          id,
          LEFTOVER_FILTER_WAIT_MS
        )
        const latest = useSessionStateStore.getState()
        outputGallery = latest.gallery
        const filterId = latest.userPreferences.filterId
        const expectedUrls = filterId
          ? filteredUrlsForSelection(photosToProcess, outputGallery.capturedItems, filterId)
          : []
        const previewSwap = await waitForValue(
          () => {
            const shown = Object.values(previewRef.current?.getPhotoStates() ?? {}).map(
              (state) => state.url
            )
            return expectedUrls.every((url) => shown.includes(url)) ? true : null
          },
          {
            timeoutMs: LEFTOVER_PREVIEW_SWAP_WAIT_MS,
            intervalMs: 100,
            shouldAbort: () => !isMountedRef.current
          }
        )
        logActivity('Strip export waited for background filter', {
          finished: leftoverWait.finished,
          filtered: leftoverWait.filtered,
          total: leftoverWait.total,
          previewUpdated: previewSwap.value === true,
          waitedMs: Date.now() - waitStartedAt
        })
        if (!isMountedRef.current || !previewRef.current) return
      }

      // Persist composition manifest before export so reconstruction is possible if export fails
      const stripId = `strip-${id}-${Date.now()}`
      const photoStates = previewRef.current.getPhotoStates() as Record<string, PhotoState>
      // Map slots to selected photos by distinct slot type (ordered by photo number, matching the
      // numeric capture order), the same way PrintPreview fills them. Indexing photosToProcess by
      // raw slot position breaks on frames with duplicate or gapped photo numbering (e.g.
      // ['photo-1','photo-1','photo-3','photo-4']) and on frames whose images are stored in
      // visual/layout order; keyed by numerically-sorted type, every slot resolves to the photo
      // that was actually captured for it.
      const manifestPhotoTypeToSelectedIndex = buildPhotoTypeToSelectedIndex(
        frameConfig.drawParams.images
      )
      const manifest: CompositionManifest = {
        manifestVersion: 1,
        sessionId: id || '',
        stripId,
        timestamp: new Date().toISOString(),
        frameId: userPreferences.frameId || '',
        frameName: selectedScaledFrame?.name,
        canvasWidth: frameConfig.drawParams.canvasWidth,
        canvasHeight: frameConfig.drawParams.canvasHeight,
        backgroundColor: frameConfig.drawParams.backgroundColor || '#FFFFFF',
        photos: frameConfig.drawParams.images
          .filter((img) => img.type.startsWith('photo-'))
          .map((imageConfig) => {
            const selIdx = manifestPhotoTypeToSelectedIndex[imageConfig.type]
            const photoUrl = (selIdx !== undefined ? photosToProcess[selIdx] : undefined) ?? ''
            const actualUrl = photoUrl?.startsWith('mirror:') ? photoUrl.slice(7) : photoUrl
            const state = photoStates[imageConfig.type]
            return {
              slotType: imageConfig.type,
              photoUrl: actualUrl,
              originalPhotoUrl: actualUrl,
              filterId: userPreferences.filterId ?? undefined,
              isMirrored: photoUrl?.startsWith('mirror:') ?? false,
              scale: state?.scale ?? 1,
              offsetX: state?.offsetX ?? 0,
              offsetY: state?.offsetY ?? 0,
              slotLeft: imageConfig.left,
              slotTop: imageConfig.top,
              slotWidth: imageConfig.width,
              slotHeight: imageConfig.height,
              rotation: imageConfig.rotation
            }
          }),
        staticAssets: frameConfig.drawParams.images
          .filter((img) => img.type === 'image' || img.type === 'qr')
          .map((img) => ({
            type: img.type,
            src: img.src,
            position: { left: img.left, top: img.top, width: img.width, height: img.height },
            rotation: img.rotation
          })),
        status: 'pending',
        retryCount: 0,
        printResolutionScale
      }
      setSessionState({
        gallery: {
          compositionManifest: manifest,
          stripGenerationStatus: 'pending'
        }
      })
      logActivity('Composition manifest saved (pending export)', { stripId, manifestVersion: 1 })

      // Export strategy: strict attempt → retry with extended timeout → degraded fallback
      // The user must ALWAYS get a strip (even if degraded), a print, and an upload.
      let exportResult: ExportResult | null = null

      // Attempt 1: strict export (all assets must be loaded)
      try {
        exportResult = await previewRef.current.exportImageWithStatus({ timeoutMs: 8000, strict: true })
      } catch (err1) {
        console.warn('[Select] Strict export failed, retrying with extended timeout:', err1 instanceof Error ? err1.message : err1)
        logActivity('Strict export failed, retrying', { error: err1 instanceof Error ? err1.message : 'unknown' })

        // Attempt 2: strict with extended timeout (network may need more time)
        try {
          exportResult = await previewRef.current.exportImageWithStatus({ timeoutMs: 15000, strict: true })
        } catch (err2) {
          console.warn('[Select] Retry export also failed, falling back to degraded export:', err2 instanceof Error ? err2.message : err2)
          logActivity('Retry export failed, falling back to degraded', { error: err2 instanceof Error ? err2.message : 'unknown' })

          // Attempt 3: degraded export (render whatever is loaded — some photos/overlays may be missing).
          // blobTimeoutMs is pixel-based and independent of timeoutMs, so a short waitForReady
          // timeout is fine here — if assets haven't loaded after two prior attempts, they won't load
          // in 60 more seconds either. Let the canvas encode with whatever is ready.
          exportResult = await previewRef.current.exportImageWithStatus({ timeoutMs: 5000, strict: false })
        }
      }

      const stripBlob = exportResult.blob
      logActivity('Strip export completed', {
        blobSize: stripBlob.size,
        frameId: userPreferences.frameId,
        photoCount: photosToProcess.length,
        assetsVerified: exportResult.assetsVerified,
        degradedReason: exportResult.degradedReason
      })

      // Mark manifest status based on export quality
      setSessionState({
        gallery: {
          compositionManifest: {
            ...manifest,
            status: exportResult.assetsVerified ? 'generated' : 'failed',
            outputBlobSize: stripBlob.size,
            failureReason: exportResult.degradedReason
          },
          stripGenerationStatus: exportResult.assetsVerified ? 'generated' : 'failed'
        }
      })
      if (!exportResult.assetsVerified) {
        logActivity('Strip exported in DEGRADED mode — reconstruction needed', {
          degradedReason: exportResult.degradedReason,
          stripId: manifest.stripId
        })
      }

      // Note: Watermarks are only applied to individual photos, not strips
      // Strips should remain clean for editing and video processing

      // If Edit is enabled, keep video processing and related steps in this page
      // Upload the plain strip first, update gallery, and start video processing
      if (editEnabled) {
        console.log('[Select] Edit enabled - uploading plain strip, deferring printing and video processing')
        
        // Upload the plain strip first (non-blocking: wait up to 2s, then continue with local URL)
        const ctx: ProcessingContext = {
          context,
          projectId,
          sessionId: id || '',
          galleryId: galleryKey || gallery.id,
          downloadLocal,
          isOnline: isOnlineRef.current
        }
        const uploadPromise = processAndUploadStrip(stripBlob, ctx)
        const timeoutPromise = new Promise<{ localUrl: string }>((resolve) => {
          setTimeout(() => resolve({ localUrl: URL.createObjectURL(stripBlob) }), 2000)
        })
        const stripResult = await Promise.race([
          uploadPromise.then((r) => (r ? { localUrl: r.localUrl } : { localUrl: URL.createObjectURL(stripBlob) })),
          timeoutPromise
        ])

        // Create local URL for Edit page to use
        const stripUrl = stripResult.localUrl || URL.createObjectURL(stripBlob)
        
        // Add plain strip to gallery
        if (stripResult.localUrl) {
          setSessionState({
            gallery: {
              capturedItems: [
                ...outputGallery.capturedItems,
                {
                  id: `dreambooth-strip-${id}`,
                  stripUrl: stripResult.localUrl,
                  photoUrl: '',
                  timestamp: new Date()
                }
              ],
              stripUrl: stripUrl
            }
          })
        } else {
          setSessionState({
            gallery: {
              stripUrl: stripUrl
            }
          })
        }
        
        console.log('[Select] Navigating to Edit page (enabled)')
        if (isMountedRef.current) {
          navigate(`/${projectId}/edit`)
        }
        return
      }

      // Edit disabled - process everything as before
      console.log('[Select] Edit disabled - processing all outputs')

      // Print strip immediately after creation (blocking for initiation only, with timeout).
      // usePrint/copies come from the store, not this render: a handler started
      // from an older render (a timer, the auto-process effect) would otherwise
      // print with the copies it saw then — 0 before the Checkout-off default
      // lands. Edit's Continue reads them the same way (useFinishEdit).
      const printPreferences = useSessionStateStore.getState().userPreferences
      didAttemptPrint = await printSessionStrip({
        page: 'select',
        stripBlob,
        usePrint: printPreferences.usePrint,
        copies: printPreferences.copies,
        frameId: userPreferences.frameId || '',
        isOnline: isOnlineRef.current,
        context: { sessionId: id || undefined, projectId: projectId || undefined },
        sentRef: printJobSentRef,
        logActivity,
        t
      })

      // Upload strip (non-blocking: wait up to 2s, then continue with local URL)
      const stripCtx: ProcessingContext = {
        context,
        projectId,
        sessionId: id || '',
        galleryId: galleryKey || gallery.id,
        downloadLocal,
        isOnline: isOnlineRef.current
      }
      const uploadPromise = processAndUploadStrip(stripBlob, stripCtx)
      const timeoutPromise = new Promise<{ localUrl: string }>((resolve) => {
        setTimeout(() => resolve({ localUrl: URL.createObjectURL(stripBlob) }), 2000)
      })
      const stripResult = await Promise.race([
        uploadPromise.then((r) => (r ? { localUrl: r.localUrl } : { localUrl: URL.createObjectURL(stripBlob) })),
        timeoutPromise
      ])

      if (stripResult.localUrl) {
        setSessionState({
          gallery: {
            capturedItems: [
              ...outputGallery.capturedItems,
              {
                id: `dreambooth-strip-${id}`,
                stripUrl: stripResult.localUrl,
                photoUrl: '',
                timestamp: new Date()
              }
            ]
          }
        })
      }

      // Generate and upload framed video in background with proper mirroring
      const effectiveRecordingType = toRecordingType(userPreferences.recordingType)
      const framedVideoOwed =
        userPreferences.useRecording && isFramedStripRecording(effectiveRecordingType)

      // Until now the only way to tell this stage apart from "never asked for a video"
      // was the ABSENCE of a later log line. A recording the guest paid for that is
      // skipped here left no trace at all — see sessions uqd410 / hz6byb.
      //
      // Only the genuinely broken state is reported: a recording was bought but no
      // recording type is set. Not `useRecording` alone (it is `true` by default and
      // for any project with `recordingEnabled`, sold or not), and not
      // `session_recording`, which Capture delivers and this stage rightly skips.
      const recordingPurchased = (
        useSessionStateStore.getState().transaction.selectedPackages ?? []
      ).some((pkg) => isRecordingPackageType(pkg.type))
      if (userPreferences.useRecording && recordingPurchased && !effectiveRecordingType) {
        logActivity('Framed strip video skipped: recording bought but no recording type set', {
          recordingType: userPreferences.recordingType ?? null,
          level: 'error'
        })
      }

      if (framedVideoOwed) {
        const resources = photosToProcess.map((photoUrl) => {
          const item = outputGallery.capturedItems.find((item) => item.photoUrl === photoUrl)
          if (!item) throw new Error(`Could not find captured item for photo ${photoUrl}`)

          // Use filtered version if filter was applied, otherwise use original photoUrl
          // This is a fallback in case Filter.tsx failed to update photoUrl (e.g., due to race condition)
          const selectedFilterId = userPreferences.filterId
          const photoToUse =
            selectedFilterId && item.filteredVersions?.[selectedFilterId]
              ? item.filteredVersions[selectedFilterId]
              : item.photoUrl

          return {
            photoUrl: photoToUse,
            videoUrl: item.recordingUrl || ''
          }
        })

        // Get photo states from PrintPreview to ensure mirroring consistency
        const photoStates = previewRef.current?.getPhotoStates() || {}
        setSessionState({
          gallery: {
            ...useSessionStateStore.getState().gallery,
            photoStates
          }
        })

        // Generate both story and live mode videos
        const taskIds = {
          sequential: `video-story-${id}-${Date.now()}`,
          live: `video-live-${id}-${Date.now()}`
        }
        const framedVideoTaskId =
          userPreferences.recordingType === 'strip_story' ? taskIds.sequential : taskIds.live
        setVideoProcessingId(framedVideoTaskId)

        // Announce the attempt. `backgroundVideoProcessor` only ever logs on completion
        // or on a thrown error, so a job that starts and quietly yields nothing was
        // indistinguishable from a job that never started.
        const clipsPresent = resources.filter((r) => r.videoUrl).length
        logActivity('Framed strip video started', {
          taskId: framedVideoTaskId,
          recordingType: userPreferences.recordingType,
          photosCount: resources.length,
          clipsPresent,
          // Every source clip empty means the per-capture recorder produced nothing
          // during capture: this job cannot deliver, and the guest already paid.
          ...(clipsPresent === 0 ? { level: 'error' } : {})
        })

        // Start framed strip video processing
        // CRITICAL: Always use unscaledFrameConfig (scale = 1) for video processing
        // Video generation will automatically resize if dimensions exceed codec limits (1920px),
        // but using unscaled frames prevents unnecessary processing and ensures consistency
        videoProcessingIdRef.current = framedVideoTaskId
        // CRITICAL: captureCountdown is in seconds, but generateFramedVideo expects milliseconds
        // Convert seconds to milliseconds (same as Capture.tsx does)
        const captureCountdownSeconds = globalConfig?.pages.capture?.settings?.captureCountdown || 3
        const captureCountdownMs = captureCountdownSeconds * 1000
        backgroundVideoProcessor.processVideo({
          id: framedVideoTaskId,
          type: userPreferences.recordingType === 'strip_story' ? 'story' : 'live',
          resources,
          frameConfig: unscaledFrameConfig, // Always unscaled - independent of print resolution
          sessionId: id || '',
          userId: context?.user?.email || '',
          projectId: projectId || '',
          galleryId: galleryKey || gallery.id,
          speed: globalConfig?.pages.capture?.settings?.recordingSpeed || 1,
          fps: 30,
          photoStates: photoStates, // Use adjusted photo states
          countdown: captureCountdownMs,
          onComplete: (uploadedUrl, localUrl) => {
            const { playbackUrl, videoRemoteUrl } = resolveFramedVideoPlaybackAndRemote(
              uploadedUrl,
              localUrl
            )
            if (!playbackUrl) return
            const prevItems = useSessionStateStore.getState().gallery.capturedItems.filter(
              (i) => i.id !== framedVideoTaskId
            )
            setSessionState({
              gallery: {
                capturedItems: [
                  ...prevItems,
                  {
                    id: framedVideoTaskId,
                    videoUrl: playbackUrl,
                    videoRemoteUrl,
                    photoUrl: '',
                    timestamp: new Date()
                  }
                ]
              }
            })
            logActivity('Framed strip video ready', {
              taskId: framedVideoTaskId,
              hasRemoteUrl: Boolean(uploadedUrl && uploadedUrl.length > 0)
            })
          },
          onError: (error) => {
            if (!isMountedRef.current) return
            
            const errorMessage = error instanceof Error ? error.message : 'Unknown error'
            console.error('[Select] Error processing video:', errorMessage, {
              taskId: framedVideoTaskId,
              recordingType: userPreferences.recordingType,
              photosCount: photosToProcess.length
            })
            logActivity(`Video processing failed: ${errorMessage}`, {
              taskId: framedVideoTaskId,
              recordingType: userPreferences.recordingType,
              photosCount: photosToProcess.length,
              error: errorMessage,
              level: 'error'
            })
            if (isMountedRef.current) {
              cogoToast.error(t('select.errorVideo', 'Error processing video'))
              setVideoProcessingId(null)
            }
            videoProcessingIdRef.current = null
          }
        })
      }

      console.log('[Select] Navigating to Result page (Edit disabled)')
      // Note: Final session upload with isCompleted will be handled by Result page
      const minDisplaySec = useSettingsStore.getState().printResultMinDisplaySeconds
      if (didAttemptPrint && minDisplaySec > 0) {
        await waitPrintResultMinDisplay(processingStartedAt, minDisplaySec, {
          isStillMounted: () => isMountedRef.current
        })
      }
      if (isMountedRef.current) {
        navigateAfterOutputsReady(navigate, projectId!, useConfigStore.getState().config)
      }
    } catch (error) {
      console.error('Error in continue process:', error)
      cogoToast.error(
        t(
          'select.errorProcessing',
          'There was an error processing your photos. Please report this issue.'
        )
      )

      // Log processing failure with structured context for diagnostics
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      logActivity(`Strip/processing failed: ${errorMessage}`, {
        photoCount: photosToProcess.length,
        frameId: userPreferences.frameId,
        hasValidFrameConfig
      })

      // Persist failed status to composition manifest for reconstruction
      const currentGallery = useSessionStateStore.getState().gallery
      const existingManifest = currentGallery.compositionManifest
      if (existingManifest) {
        setSessionState({
          gallery: {
            compositionManifest: {
              ...existingManifest,
              status: 'failed',
              failureReason: errorMessage,
              retryCount: (existingManifest.retryCount ?? 0) + 1
            },
            stripGenerationStatus: 'failed'
          }
        })
        logActivity('Composition manifest updated (failed)', {
          stripId: existingManifest.stripId,
          failureReason: errorMessage
        })
      }
      // Check if Edit page is enabled - use both methods for reliability
      const editSettings = useConfigStore.getState().getPageSettings('edit')
      const editEnabled =
        editSettings?.enabled ?? globalConfig?.pages.edit?.settings?.enabled ?? false

      if (editEnabled) {
        console.log('[Select] Error handler: Navigating to Edit page (enabled)')
        if (isMountedRef.current) {
          navigate(`/${projectId}/edit`)
        }
      } else {
        console.log('[Select] Error handler: Navigating to Result page (Edit disabled)')
        if (isMountedRef.current) {
          navigateAfterOutputsReady(navigate, projectId!, useConfigStore.getState().config)
        }
      }
    } finally {
      if (isMountedRef.current) {
        setIsUploading(false)
        setIsSelectionLocked(false)
      }
      isProcessingRef.current = false
    }
  }

  // Set default copies when checkout is disabled but usePrint is true
  useEffect(() => {
    const checkoutSettings = getPageSettings('checkout')
    const checkoutEnabled = checkoutSettings?.enabled ?? globalConfig?.pages.checkout?.settings?.enabled ?? true

    // With no checkout page the guest never states what they bought, so the booth
    // falls back to one print. A project whose package list is set but contains no
    // `print` row has *declared* itself digital-only (attended / open-booth flows,
    // where prints are decided afterwards via the free reprint modal) — honour that
    // instead of printing a copy nobody asked for. An empty package list states no
    // intent at all, so it keeps the historical one-copy fallback.
    const checkoutPackages =
      checkoutSettings?.packages ?? globalConfig?.pages.checkout?.settings?.packages ?? []
    const isDigitalOnly =
      checkoutPackages.length > 0 && !checkoutPackages.some((pkg) => pkg.type === 'print')

    // If checkout is disabled and usePrint is true but copies is 0, set default copies to 1
    if (!checkoutEnabled && userPreferences.usePrint && userPreferences.copies === 0) {
      if (isDigitalOnly) {
        console.log('[Select] Checkout disabled and packages are digital-only - not printing')
        setSessionState({
          userPreferences: {
            usePrint: false
          }
        })
        logActivity('Digital-only packages (checkout disabled) — skipping automatic print')
        return
      }
      console.log('[Select] Checkout disabled but usePrint is true - setting default copies to 1')
      setSessionState({
        userPreferences: {
          copies: 1
        }
      })
      logActivity('Set default copies to 1 (checkout disabled, usePrint enabled)')
    }
  }, [globalConfig?.pages.checkout?.settings?.enabled, userPreferences.usePrint, userPreferences.copies])

  // Initialize countdown and handle initial media uploads
  useEffect((): void | (() => void) => {
    isMountedRef.current = true
    
    // Upload session state on mount (fire-and-forget)
    stateUploader.uploadSessionState().catch(() => {})

    // Only use individual countdown in standard mode
    // In frame-based mode, use shared countdown only
    if (captureMode === 'standard') {
      const selectionTimeout = config?.settings?.selectionTimeout
      if (selectionTimeout && selectionTimeout > 0) {
        // Set up countdown callbacks
        selectionCountdown.setOnTick(() => {
          setRemainingTime(selectionCountdown.getFormattedRemainingSeconds())
        })

        selectionCountdown.setOnComplete(() => {
          handleCountdownCompleteRef.current?.()
        })

        selectionCountdown.start()
      }
    }

    // Initialize media uploads
    const initializeMedia = async (): Promise<void> => {
      // Prevent concurrent upload attempts
      if (uploadStartedRef.current) {
        return
      }
      uploadStartedRef.current = true

      // Slow booths only: photos the Filter page ran out of time for are still being
      // filtered, so the GIF and the photo upload wait for them. Without leftovers nothing
      // waits, and the two helpers below give exactly what this code used before.
      const waitedForFilter = backgroundFilterProcessor.hasLeftovers(id)
      if (waitedForFilter) {
        await backgroundFilterProcessor.waitForLeftovers(id, LEFTOVER_FILTER_WAIT_MS)
      }
      // After a wait the mount-time `gallery` lacks the late filtered versions, so read the
      // store, unless the session has ended meanwhile: then the mount-time snapshot is all
      // that is left of this session.
      const readStoreAfterWait = (): boolean =>
        waitedForFilter && useSessionStateStore.getState().id === id
      const mediaGallery = (): typeof gallery =>
        readStoreAfterWait() ? useSessionStateStore.getState().gallery : gallery
      // Select may be gone by the time the media is ready; the session still wants it.
      const stillWanted = (): boolean =>
        waitedForFilter ? useSessionStateStore.getState().id === id : isMountedRef.current

      // Create and upload both MP4 and GIF formats if enabled - fire in background
      if (userPreferences.useGIF) {
        (async (): Promise<void> => {
          const selectedFilterId = userPreferences.filterId
          const photoUrls = mediaGallery().capturedItems
            .filter((item) => item.photoUrl)
            .map((item) =>
              selectedFilterId && item.filteredVersions?.[selectedFilterId]
                ? item.filteredVersions[selectedFilterId]
                : item.photoUrl
            ) as string[]
          const gifSpeed = globalConfig?.pages.capture?.settings?.gifSpeed || 1000
          const customGifFrame = globalConfig?.pages.capture?.settings?.customGifFrame
          const initCtx: ProcessingContext = {
            context,
            projectId,
            sessionId: id || '',
            galleryId: galleryKey || gallery.id,
            downloadLocal,
            isOnline: isOnlineRef.current
          }
          const result = await processAndUploadGIF(
            photoUrls,
            gifSpeed,
            initCtx,
            customGifFrame,
            (gifLocalUrl, mp4LocalUrl) => {
              if (!stillWanted()) return
              const newItems: Array<{
                id: string
                animationUrl?: string
                animationType?: 'mp4' | 'gif'
                gifUrl?: string
                photoUrl: string
                timestamp: Date
              }> = []
              if (mp4LocalUrl) {
                newItems.push({
                  id: `dreambooth-animation-mp4-${id}`,
                  animationUrl: mp4LocalUrl,
                  animationType: 'mp4',
                  gifUrl: '',
                  photoUrl: '',
                  timestamp: new Date()
                })
              }
              if (gifLocalUrl) {
                newItems.push({
                  id: `dreambooth-animation-gif-${id}`,
                  animationUrl: gifLocalUrl,
                  animationType: 'gif',
                  gifUrl: gifLocalUrl,
                  photoUrl: '',
                  timestamp: new Date()
                })
              }
              if (newItems.length > 0) {
                setSessionState({
                  gallery: {
                    capturedItems: [...mediaGallery().capturedItems, ...newItems]
                  }
                })
              }
            }
          )

          if (isMountedRef.current) {
            const mp4Failed = !result.mp4LocalUrl
            const gifFailed = !result.gifLocalUrl
            if (mp4Failed && gifFailed) {
              cogoToast.error('Failed to create both animation formats')
            } else if (mp4Failed) {
              cogoToast.warn('MP4 creation failed, but GIF was created successfully')
            } else if (gifFailed) {
              cogoToast.warn('GIF creation failed, but MP4 was created successfully')
            }
          }
        })()
        // Continue immediately without waiting
      }

      // Upload all photos in background - fire-and-forget
      const { gallery: latestGallery, userPreferences: latestPrefs } =
        waitedForFilter && !readStoreAfterWait()
          ? { gallery, userPreferences }
          : useSessionStateStore.getState()
      const selectedFilterId = latestPrefs?.filterId
      // Build the filtered list of source items first so we can assign each one a
      // stable `captureIndex` that matches the `${index}` baked into its eventual
      // upload filename (`dreambooth-${sessionId}-photo-${index}.jpg`). Post-session AI
      // uses this to map server outputs back to the originating gallery item — see
      // `CapturedItem.captureIndex` and `PostSessionAI.onApply`.
      const filteredSourceItems = latestGallery.capturedItems.filter((item) => {
        const url = selectedFilterId && item.filteredVersions?.[selectedFilterId]
          ? item.filteredVersions[selectedFilterId]
          : item.photoUrl
        return url && url.trim() !== ''
      })
      const photoItems = filteredSourceItems.map((item) => ({
        photoUrl: (selectedFilterId && item.filteredVersions?.[selectedFilterId]
          ? item.filteredVersions[selectedFilterId]
          : item.photoUrl) as string
      }))
      if (photoItems.length > 0) {
        const photosCtx: ProcessingContext = {
          context,
          projectId,
          sessionId: id || '',
          galleryId: galleryKey || gallery.id,
          downloadLocal: false,
          isOnline: isOnlineRef.current
        }
        // Pre-write captureIndex so it survives even if the upload fails: PostSessionAI
        // can still target the right item by id, and the server resolves the actual
        // HTTPS URL by sessionId on retry.
        const idToCaptureIndex = new Map<string, number>()
        filteredSourceItems.forEach((item, idx) => {
          if (item.id) idToCaptureIndex.set(item.id, idx)
        })
        if (idToCaptureIndex.size > 0) {
          const g = useSessionStateStore.getState().gallery
          setSessionState({
            gallery: {
              ...g,
              capturedItems: g.capturedItems.map((it) => {
                const idx = idToCaptureIndex.get(it.id)
                return idx === undefined ? it : { ...it, captureIndex: idx }
              })
            }
          })
        }
        if (readStoreAfterWait()) {
          // Select owns this upload from here on (failed files go to the retry queue).
          // Mark it now: the wait pushed the start late enough that Edit's finish could
          // otherwise upload every photo a second time, with the leftovers unfiltered.
          const g = useSessionStateStore.getState().gallery
          setSessionState({
            gallery: {
              ...g,
              individualPhotosUploaded: true,
              individualPhotosUploadedAt: Date.now()
            }
          })
        }
        // Without a wait the done flag lands only after every upload returns, by which time
        // the guest is usually on Edit, so Edit uploaded every photo again. Record the start
        // so it can tell (see editShouldUploadPhotos).
        if (useSessionStateStore.getState().id === id) {
          const g = useSessionStateStore.getState().gallery
          setSessionState({
            gallery: {
              ...g,
              individualPhotosUploadStartedAt: Date.now()
            }
          })
        }
        processAndUploadPhotos(photoItems, photosCtx)
          .then(() => {
            if (!stillWanted()) return
            const g = useSessionStateStore.getState().gallery
            setSessionState({
              gallery: {
                ...g,
                individualPhotosUploaded: true,
                individualPhotosUploadedAt: Date.now()
              }
            })
          })
          .catch((err) => {
            console.warn('[Select] Individual photos upload failed:', err)
          })
      }
      // Continue immediately without waiting
    }

    initializeMedia()

    return (): void => {
      isMountedRef.current = false
      if (captureMode === 'standard') {
        selectionCountdown.stop()
        selectionCountdown.setOnComplete(null)
        selectionCountdown.setOnTick(null)
      }
    }
  }, [captureMode])

  // Handle global countdown expiration - auto-continue
  // When timeout expires, auto-select photos if needed, then auto-process
  useEffect(() => {
    if (
      captureMode === 'frame-based' &&
      isSharedCountdownExpired &&
      !isUploading &&
      !navigationLockRef.current &&
      isMountedRef.current
    ) {
      navigationLockRef.current = true
      logActivity('Global countdown expired on Select page, auto-continuing')
      
      // Auto-select photos if none are selected or if partial selection
      if (selectedPhotos.length === 0 || selectedPhotos.length < maxSelectablePhotos) {
        const photosOnly = gallery.capturedItems.filter(
          (item) =>
            item.photoUrl &&
            !item.id?.includes('dreambooth-strip') &&
            !item.id?.includes('dreambooth-gif')
        )
        
        // Get photos that are already selected
        const alreadySelected = new Set(selectedPhotos)
        
        // Get remaining photos needed
        const remainingNeeded = maxSelectablePhotos - selectedPhotos.length
        const unselectedPhotos = photosOnly
          .filter((item) => !alreadySelected.has(item.photoUrl || ''))
          .slice(0, remainingNeeded)
        const autoSelected = unselectedPhotos.map((item) => item.photoUrl || '')
        
        // Combine existing selection with auto-selected photos
        const finalSelection = [...selectedPhotos, ...autoSelected]
        
        setSelectedPhotos(finalSelection)
        
        // Wait for selection to update, then process
        setTimeout(() => {
          if (isMountedRef.current) {
            handleCountdownComplete()
          }
        }, 100)
      } else {
        // Photos already fully selected, process immediately
        handleCountdownComplete()
      }
      
      // Reset lock after navigation completes
      setTimeout(() => {
        navigationLockRef.current = false
      }, 2000)
    }
  }, [captureMode, isSharedCountdownExpired, selectedPhotos.length, isUploading, gallery.capturedItems, maxSelectablePhotos])

  // Check internet connectivity for offline detection
  useEffect(() => {
    if (typeof window.api !== 'undefined' && window.api.getInternetStatus) {
      window.api.getInternetStatus().then((status: boolean) => {
        isOnlineRef.current = status
      })

      const unsubscribe = window.api.onInternetStatusChanged((status: boolean) => {
        isOnlineRef.current = status
      })

      return unsubscribe
    }
    return (): void => {}
  }, [])

  // Add effect to check video processing status
  useEffect(() => {
    if (!videoProcessingId) return

    const checkStatus = (): void => {
      const isProcessing = backgroundVideoProcessor.isTaskProcessing(videoProcessingId)
      if (!isProcessing) {
        if (isMountedRef.current) {
          setVideoProcessingId(null)
        }
        videoProcessingIdRef.current = null
      }
    }

    const interval = setInterval(checkStatus, 1000)
    return (): void => clearInterval(interval)
  }, [videoProcessingId])
  
  // Cleanup video processing on unmount
  useEffect(() => {
    return (): void => {
      if (videoProcessingIdRef.current) {
        // Clear ref - video processing will continue in background but won't update state
        videoProcessingIdRef.current = null
      }
    }
  }, [])

  // Wait for PrintPreview to be ready (deps must stay stable — see memoized frameConfig above)
  useEffect(() => {
    setIsPreviewReady(false)
    let cancelled = false

    const waitForPreview = async (): Promise<void> => {
      // Ref is attached after PrintPreview mounts; give the tree a couple of frames.
      for (let i = 0; i < 20 && !previewRef.current; i++) {
        await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()))
        if (cancelled) return
      }
      if (!previewRef.current) {
        console.warn('[Select] PrintPreview ref not ready; allowing continue after timeout')
        setTimeout(() => {
          if (!cancelled && isMountedRef.current) {
            setIsPreviewReady(true)
          }
        }, 2000)
        return
      }
      try {
        await previewRef.current.waitForReady()
        if (!cancelled && isMountedRef.current) {
          setIsPreviewReady(true)
        }
      } catch (error) {
        console.error('Error waiting for preview to be ready:', error)
        setTimeout(() => {
          if (!cancelled && isMountedRef.current) {
            setIsPreviewReady(true)
          }
        }, 5000)
      }
    }
    void waitForPreview()
    return (): void => {
      cancelled = true
    }
  }, [selectedPhotos, preferredFrameId, printResolutionScale])

  const handlePhotoClick = (photoUrl: string): void => {
    if (isSelectionLocked) return

    setSelectedPhotos((prev) => {
      let newSelection
      if (prev.includes(photoUrl)) {
        newSelection = prev.filter((url) => url !== photoUrl)
      } else if (prev.length < maxSelectablePhotos) {
        newSelection = [...prev, photoUrl]
      } else {
        newSelection = prev
      }
      return newSelection
    })
  }

  // Modified handleContinue to use the new function
  const handleContinue = async (): Promise<void> => {
    // Check if preview is ready, show warning if not
    if (!isPreviewReady) {
      cogoToast.warn('Preview is still loading. Please wait...')
      return
    }

    await handleContinueWithPhotos(selectedPhotos)
  }

  // Separate countdown component to prevent preview re-renders
  // Only show individual countdown in standard mode
  const CountdownDisplay = React.memo(() => {
    if (captureMode === 'frame-based') {
      return null // Don't show individual countdown in frame-based mode
    }
    return (
      <span
        className={`shrink-0 font-mono tabular-nums text-carbon ${
          selectMobileLandscape
            ? 'text-sm'
            : isCompact
              ? 'text-base'
              : 'text-3xl'
        }`}
      >
        {remainingTime}
      </span>
    )
  })
  CountdownDisplay.displayName = 'CountdownDisplay'

  // Update the loading state to include video processing
  const isLoading: boolean = isUploading || videoProcessingId !== null

  // Update the button text to show video processing state
  const buttonText: string = isLoading
    ? videoProcessingId
      ? t('select.processingVideo', 'Processing Video...')
      : t('select.processing', 'Processing...')
    : t('select.next', 'Next ({{count}}/{{max}})', {
        count: selectedPhotos.length,
        max: maxSelectablePhotos
      })

  // Auto-select and auto-process photos when Select is disabled
  useEffect(() => {
    if (!config?.settings?.enabled && selectedPhotos.length > 0 && !isUploading) {
      // Auto-process when photos are selected and page is disabled
      const autoProcess = async (): Promise<void> => {
        try {
          if (previewRef.current) {
            // Wait for PrintPreview to be ready with timeout
            try {
              await Promise.race([
                previewRef.current.waitForReady(),
                new Promise<never>((_, reject) =>
                  setTimeout(() => reject(new Error('PrintPreview timeout')), 10000)
                )
              ])
            } catch (error) {
              console.warn('[Select] PrintPreview waitForReady timeout or error:', error)
              // Continue anyway - PrintPreview might still work
            }
          }
          // Small delay to ensure everything is ready
          await new Promise((resolve) => setTimeout(resolve, 500))
          await handleContinueWithPhotos(selectedPhotos)
        } catch (error) {
          console.error('[Select] Error in auto-process (disabled mode):', error)
          // Fallback: process anyway
          await handleContinueWithPhotos(selectedPhotos)
        }
      }
      autoProcess()
    }
  }, [config?.settings?.enabled, selectedPhotos.length, isUploading])

  // Auto-select photos when Select is disabled
  useEffect(() => {
    if (!config?.settings?.enabled && selectedPhotos.length === 0) {
      // Calculate how many photos we need based on frame configuration
      const photoSlots = frameConfig.drawParams.images.filter((img) =>
        img.type.startsWith('photo-')
      ).length

      // Auto select first N photos
      const autoSelected = gallery.capturedItems.slice(0, photoSlots)
      const selectedUrls = autoSelected.map((item) => item.photoUrl)
      setSelectedPhotos(selectedUrls)
    }
  }, [config?.settings?.enabled, frameConfig, gallery.capturedItems, selectedPhotos.length])

  // Frame-based mode: Auto-select photos based on frame configuration
  useEffect(() => {
    if (captureMode === 'frame-based' && selectedFrame && selectedPhotos.length === 0) {
      // Auto-select photos based on frame configuration
      const photosOnly = gallery.capturedItems.filter(
        (item) =>
          item.photoUrl &&
          !item.id?.includes('dreambooth-strip') &&
          !item.id?.includes('dreambooth-gif')
      )

      const hasPhotoTypeMetadata = photosOnly.some((item) => !!item.photoType)
      const autoSelected = hasPhotoTypeMetadata
        ? Array.from({ length: maxSelectablePhotos })
            .map((_, i) => `photo-${i + 1}`)
            .map((photoType) => {
              const match = photosOnly.find((it) => it.photoType === photoType)
              return match?.photoUrl || ''
            })
            .filter(Boolean)
        : photosOnly.slice(0, maxSelectablePhotos).map((item) => item.photoUrl || '')
      
      if (autoSelected.length === maxSelectablePhotos) {
        setSelectedPhotos(autoSelected)
      }
    }
  }, [captureMode, selectedFrame, gallery.capturedItems, maxSelectablePhotos, selectedPhotos.length])

  // If select page is disabled, show only processing modal
  if (!config?.settings?.enabled) {
    return (
      <BackgroundContainer className="relative">
        {captureMode === 'frame-based' && !stackSelectTop && renderSharedSessionTimer()}
        <ProcessingModal
          isVisible={true}
          title={t('select.processing', 'Processing...')}
          message={t('select.pleaseWait', 'Please wait while we process your photos...')}
          primaryColor={primaryColor}
        />
        {/* Hidden PrintPreview for strip export */}
        <div
          style={{
            position: 'absolute',
            visibility: 'hidden',
            width: `${frameConfig.drawParams.canvasWidth}px`,
            height: `${frameConfig.drawParams.canvasHeight}px`,
            top: '-9999px',
            left: '-9999px'
          }}
        >
          <PrintPreview
            ref={previewRef}
            frameConfig={frameConfig}
            selectedPhotos={selectedPhotos.length > 0 ? selectedPhotos.map((photo) => {
              const resolvedPhoto = resolvePhotoUrl(photo)
              return mirroredPhotos.has(photo) ? `mirror:${resolvedPhoto}` : resolvedPhoto
            }) : gallery.capturedItems.filter((item) => item.photoUrl).slice(0, maxSelectablePhotos).map((item) => {
              const photoUrl = item.photoUrl || ''
              return resolvePhotoUrl(photoUrl)
            })}
            isInteractive={false}
            sessionId={id || ''}
          />
        </div>
      </BackgroundContainer>
    )
  }

  // Frame-based mode UI - centered PrintPreview with mirror button and drag/zoom
  if (captureMode === 'frame-based' && selectedFrame) {
    // Check if Edit page is enabled
    const editSettings = useConfigStore.getState().getPageSettings('edit')

    // Get photos for frame-based mode
    const frameBasedPhotos = selectedPhotos.length > 0
      ? selectedPhotos
      : ((): string[] => {
          const photosOnly = gallery.capturedItems.filter(
            (item) =>
              item.photoUrl &&
              !item.id?.includes('dreambooth-strip') &&
              !item.id?.includes('dreambooth-gif')
          )
          const hasPhotoTypeMetadata = photosOnly.some((item) => !!item.photoType)
          if (!hasPhotoTypeMetadata) {
            return photosOnly.slice(0, maxSelectablePhotos).map((item) => item.photoUrl || '')
          }
          return Array.from({ length: maxSelectablePhotos })
            .map((_, i) => `photo-${i + 1}`)
            .map((photoType) => {
              const match = photosOnly.find((it) => it.photoType === photoType)
              return match?.photoUrl || ''
            })
            .filter(Boolean)
        })()

    return (
      <BackgroundContainer className="relative flex h-full min-h-0 flex-col gap-4 sm:gap-6">
        {!stackSelectTop && renderSharedSessionTimer()}
        {/* Standardized ProcessingModal for loading states */}
        <ProcessingModal
          isVisible={isUploading || videoProcessingId !== null}
          title={
            videoProcessingId
              ? t('select.processingVideo', 'Processing Video...')
              : t('select.processing', 'Processing...')
          }
          message={t('select.pleaseWait', 'Please wait while we process your photos...')}
          primaryColor={primaryColor}
        />

        {/* Header with Mirror Button */}
        <div
          className={`flex shrink-0 items-center justify-center ${stackSelectTop ? 'mt-3 px-4' : selectLandscapeCompact ? 'mt-4 px-4' : 'mt-4 px-12'}`}
        >
          <button
            type="button"
            onClick={() => {
              if (isSelectionLocked) return
              // Mirror all photos
              const newMirroredPhotos = new Set(mirroredPhotos)
              const willMirrorAll = frameBasedPhotos.some(
                (photo) => !mirroredPhotos.has(photo)
              )

              frameBasedPhotos.forEach((photo) => {
                if (willMirrorAll) {
                  newMirroredPhotos.add(photo)
                } else {
                  newMirroredPhotos.delete(photo)
                }
              })

              setIsAllMirrored(willMirrorAll)
              setMirroredPhotos(newMirroredPhotos)
            }}
            disabled={isSelectionLocked}
            title={
              isSelectionLocked
                ? t('select.selectionLocked', 'Selection locked')
                : t('select.mirror', 'Mirror Photos')
            }
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors shadow-md ${
              isAllMirrored
                ? 'text-white hover:bg-opacity-90'
                : 'bg-white hover:bg-gray-100 text-carbon'
            } ${isSelectionLocked ? 'opacity-50 cursor-not-allowed' : ''}`}
            style={{
              backgroundColor: isAllMirrored ? primaryColor : undefined
            }}
          >
            <FaExchangeAlt className={`text-lg ${isAllMirrored ? 'text-white' : 'text-carbon'}`} />
            <span className={isAllMirrored ? 'text-sm text-white' : 'text-sm text-carbon'}>
              {t('select.mirror', 'Mirror')}
            </span>
          </button>
        </div>

        {/* Centered PrintPreview with drag and zoom — aspect box fits full strip (no clip) */}
        <div
          className={`flex min-h-0 min-w-0 flex-1 items-center justify-center overflow-hidden ${
            isCompact
              ? stackSelectTop
                ? 'px-2 pb-32'
                : 'px-2'
              : 'px-8'
          }`}
        >
          <div className="flex h-full w-full min-h-0 min-w-0 items-center justify-center">
            <div
              className="max-h-full max-w-full min-h-0 min-w-0"
              style={{
                aspectRatio: `${frameConfig.drawParams.canvasWidth} / ${frameConfig.drawParams.canvasHeight}`
              }}
            >
              <PrintPreview
                ref={previewRef}
                frameConfig={frameConfig}
                selectedPhotos={frameBasedPhotos.map((photo) => {
                  const resolvedPhoto = resolvePhotoUrl(photo)
                  return mirroredPhotos.has(photo) ? `mirror:${resolvedPhoto}` : resolvedPhoto
                })}
                isInteractive={true}
                className="h-full w-full shadow-xl"
                sessionId={id || ''}
              />
            </div>
          </div>
        </div>

        {stackSelectTop && (
          <div className="flex shrink-0 flex-col items-center gap-1 px-2 pt-1">
            {renderSharedSessionTimerBelowStrip()}
          </div>
        )}

        {!stackSelectTop && (
          <div className="flex flex-shrink-0 flex-col items-center gap-4 pb-8">
            <Button
              text={
                isLoading
                  ? videoProcessingId
                    ? t('select.processingVideo', 'Processing Video...')
                    : t('select.processing', 'Processing...')
                  : t('select.continue', 'Continue')
              }
              onClick={handleContinue}
              data-nav-to={editSettings?.enabled ? 'edit' : 'result'}
              disabled={
                frameBasedPhotos.length !== maxSelectablePhotos || isUploading || !isPreviewReady
              }
              variant={
                frameBasedPhotos.length === maxSelectablePhotos && !isUploading && isPreviewReady
                  ? 'primary'
                  : 'secondary'
              }
            />
          </div>
        )}
        {stackSelectTop && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[100] flex justify-end p-0">
            <div className="pointer-events-auto pr-4 pb-12">
              <Button
                text={
                  isLoading
                    ? videoProcessingId
                      ? t('select.processingVideo', 'Processing Video...')
                      : t('select.processing', 'Processing...')
                    : t('select.continue', 'Continue')
                }
                onClick={handleContinue}
                data-nav-to={editSettings?.enabled ? 'edit' : 'result'}
                disabled={
                  frameBasedPhotos.length !== maxSelectablePhotos || isUploading || !isPreviewReady
                }
                variant={
                  frameBasedPhotos.length === maxSelectablePhotos && !isUploading && isPreviewReady
                    ? 'primary'
                    : 'secondary'
                }
                className={`${selectNextStackedBtn} shadow-lg`}
              />
            </div>
          </div>
        )}
      </BackgroundContainer>
    )
  }

  const stackSelectPhotoCount = React.useMemo(
    () => gallery.capturedItems.filter((item) => !!item.photoUrl).length,
    [gallery.capturedItems]
  )
  const stackSelectGridColsClass =
    stackSelectPhotoCount <= 1
      ? 'grid-cols-1'
      : stackSelectPhotoCount === 2
        ? 'grid-cols-2'
        : stackSelectPhotoCount === 3
          ? 'grid-cols-3'
          : 'grid-cols-4'
  const gridCols = stackSelectTop
    ? stackSelectGridColsClass
    : isPotrait
      ? 'grid-cols-1'
      : isCompact
        ? 'grid-cols-3'
        : 'grid-cols-3'
  const gridGap = stackSelectTop ? 'gap-1.5' : selectLandscapeCompact ? 'gap-2' : 'gap-6'

  const previewWidthFixed = isTallFrame ? 'w-[300px]' : 'w-[426px]'

  const photoSelectionBlock = (
    <div
      className={`flex w-full min-h-0 flex-col ${stackSelectTop ? 'max-w-full flex-1' : 'max-w-[70%] flex-1'}`}
    >
      <div
        className={`flex items-center justify-center ${stackSelectTop ? 'mb-2 px-4 py-2' : selectLandscapeCompact ? 'mb-3 mt-4 px-3 py-1' : 'mb-8 mt-16 px-12'}`}
      >
        <button
          type="button"
          onClick={() => {
            if (isSelectionLocked) return
            const newMirroredPhotos = new Set(mirroredPhotos)
            const willMirrorAll = gallery.capturedItems.some(
              (item) => !mirroredPhotos.has(item.photoUrl)
            )

            gallery.capturedItems.forEach((item) => {
              if (willMirrorAll) {
                newMirroredPhotos.add(item.photoUrl)
              } else {
                newMirroredPhotos.delete(item.photoUrl)
              }
            })

            setIsAllMirrored(willMirrorAll)
            setMirroredPhotos(newMirroredPhotos)
          }}
          disabled={isSelectionLocked}
          title={
            isSelectionLocked
              ? t('select.selectionLocked', 'Selection locked')
              : t('select.mirror', 'Mirror Photos')
          }
          className={`flex items-center gap-2 rounded-lg transition-colors shadow-md ${
            stackSelectTop ? 'px-3 py-1.5' : 'px-4 py-2'
          } ${
            isAllMirrored
              ? 'text-white hover:bg-opacity-90'
              : 'bg-white hover:bg-gray-100 text-carbon'
          } ${isSelectionLocked ? 'cursor-not-allowed opacity-50' : ''}`}
          style={{
            backgroundColor: isAllMirrored ? primaryColor : undefined
          }}
        >
          <FaExchangeAlt
            className={`${stackSelectTop ? 'text-base' : 'text-lg'} ${isAllMirrored ? 'text-white' : 'text-carbon'}`}
          />
          <span
            className={`${stackSelectTop ? 'text-xs' : 'text-sm'} ${isAllMirrored ? 'text-white' : 'text-carbon'}`}
          >
            {t('select.mirror', 'Mirror')}
          </span>
        </button>
      </div>
      <div
        className={`relative flex-1 overflow-y-auto ${stackSelectTop ? 'px-3 pb-28 pt-1' : selectLandscapeCompact ? 'px-3 pb-3' : 'px-16 pb-12'} ${isSelectionLocked ? 'pointer-events-none' : ''}`}
      >
        {isSelectionLocked && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-carbon/5 backdrop-blur-[1px]">
            <div className="rounded-lg bg-white/90 px-6 py-3 shadow-lg">
              <span className={`text-carbon ${stackSelectTop ? 'text-base' : 'text-xl'}`}>
                {isUploading
                  ? t('select.processing', 'Processing photos...')
                  : t('select.selectionLocked', 'Selection locked')}
              </span>
            </div>
          </div>
        )}
        <div className={`grid ${gridCols} ${gridGap}`} role="listbox">
          {gallery.capturedItems.map((item, index) => {
            if (!item.photoUrl) return null
            const isSelected = selectedPhotos.includes(item.photoUrl)
            const isMirrored = mirroredPhotos.has(item.photoUrl)
            return (
              <div key={index} className="group relative">
                <button
                  type="button"
                  onClick={() => handlePhotoClick(item.photoUrl)}
                  disabled={isSelectionLocked}
                  role="option"
                  className={`
                      group relative w-full overflow-hidden rounded-xl transition-all duration-300
                      ${isSelected ? 'scale-95 ring-4' : 'hover:scale-[0.98]'}
                      ${isSelectionLocked ? 'cursor-not-allowed opacity-75' : ''}
                    `}
                  style={{
                    boxShadow: isSelected ? `0 0 0 4px ${primaryColor}` : ''
                  }}
                >
                  <PhotoFrame
                    src={item.photoUrl || ''}
                    alt={t('capture.lastCaptured', 'Capture {{number}}', { number: index + 1 })}
                    displayRatio={userPreferences.framePhotoRatio || 1}
                    isMirrored={isMirrored}
                    className={isSelected ? 'grayscale' : ''}
                  />
                  <div
                    className={`
                        absolute inset-0 flex items-center justify-center bg-black/50 transition-opacity duration-300
                        ${isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}
                      `}
                  >
                    <div className="text-center text-white">
                      <div className={`${stackSelectTop ? 'mb-0.5 text-lg' : 'mb-2 text-4xl'}`}>
                        {isSelected ? '✓' : '+'}
                      </div>
                      <p className={`font-medium ${stackSelectTop ? 'text-[10px] leading-tight' : 'text-lg'}`}>
                        {isSelected
                          ? t('select.selected', 'Selected')
                          : t('select.clickToSelect', 'Click to Select')}
                      </p>
                    </div>
                  </div>
                  {isSelected && (
                    <div
                      className={`absolute right-1 top-1 flex items-center justify-center rounded-full shadow-lg ${stackSelectTop ? 'h-5 w-5' : 'h-8 w-8'}`}
                      style={{
                        backgroundColor: primaryColor
                      }}
                    >
                      <span
                        style={{
                          color: secondaryColor
                        }}
                        className={`font-bold ${stackSelectTop ? 'text-xs' : 'text-lg'}`}
                      >
                        {selectedPhotos.indexOf(item.photoUrl || '') + 1}
                      </span>
                    </div>
                  )}
                </button>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )

  const previewPanel = (
    <GlassPanel
      className={`flex w-full flex-col items-center ${
        stackSelectTop
          ? 'max-h-[58%] min-h-[40%] max-w-full shrink-0 gap-3 overflow-hidden p-2'
          : selectLandscapeCompact
            ? 'h-full max-w-[40%] min-h-0 flex-1 md:gap-3 overflow-hidden p-2'
            : isPotrait
              ? `h-full max-w-[55%] min-h-0 overflow-y-auto ${isCompact ? 'p-8' : 'p-12'} ${selectPortraitSideBySide ? 'justify-center gap-7' : 'gap-4'}`
              : 'h-full max-w-[35%] min-h-0 gap-4 overflow-hidden p-8'
      }`}
    >
      <div
        className={`flex w-full shrink-0 flex-col items-center ${
          stackSelectTop
            ? 'mt-1 gap-1 px-2'
            : selectLandscapeCompact
              ? 'mt-2 gap-3 px-2'
              : selectPortraitSideBySide
                ? 'mt-0 gap-4 px-4'
                : selectMobileLandscape
                  ? 'mt-6 gap-3 px-4'
                  : 'mt-8 gap-4 px-4'
        }`}
      >
        <h1
          className={`text-center font-bold text-carbon ${stackSelectTop ? 'text-xl leading-tight' : isCompact ? 'text-3xl' : 'text-4xl'}`}
        >
          {t('select.printPhotos', 'Print Photos')}
        </h1>
        <p
          className={`max-w-full text-center text-carbon/60 leading-snug ${
            stackSelectTop
              ? 'px-1 text-xs'
              : selectLandscapeCompact || selectPortraitSideBySide || selectMobileLandscape
                ? 'px-4 text-sm'
                : 'px-4 text-base'
          }`}
        >
          {/* Lead with the actual task — the gesture hint alone left the core
              interaction (tap rail photos to fill slots) unexplained (wave-1 audit). */}
          {t('select.tapToFill', 'Tap your photos to fill the print slots')}
          {' · '}
          {t('select.swipeInstructions', 'Swipe to move, pinch to zoom')}
        </p>
      </div>
      <div
        className={`flex w-full min-h-0 items-center justify-center overflow-hidden ${
          selectPortraitSideBySide ? 'shrink' : 'flex-1'
        } ${
          selectPreviewAspectBox ? 'min-h-0 px-1 py-2' : ''
        } ${isTallFrame && !selectPreviewAspectBox ? 'overflow-y-auto' : ''}`}
      >
        {selectPreviewAspectBox ? (
          <div className="flex h-full w-full min-h-0 min-w-0 items-center justify-center">
            <div
              className="max-h-full max-w-full min-h-0 min-w-0"
              style={{
                aspectRatio: `${frameConfig.drawParams.canvasWidth} / ${frameConfig.drawParams.canvasHeight}`
              }}
            >
              <PrintPreview
                ref={previewRef}
                frameConfig={frameConfig}
                selectedPhotos={selectedPhotos.map((photo) => {
                  const resolvedPhoto = resolvePhotoUrl(photo)
                  return mirroredPhotos.has(photo) ? `mirror:${resolvedPhoto}` : resolvedPhoto
                })}
                className="h-full w-full min-h-0 min-w-0 shadow-xl"
                sessionId={id || ''}
              />
            </div>
          </div>
        ) : (
          <div
            className={`${previewWidthFixed} shrink-0 px-4 ${selectPortraitSideBySide ? 'max-h-full min-h-0' : ''}`}
          >
            <PrintPreview
              ref={previewRef}
              frameConfig={frameConfig}
              selectedPhotos={selectedPhotos.map((photo) => {
                const resolvedPhoto = resolvePhotoUrl(photo)
                return mirroredPhotos.has(photo) ? `mirror:${resolvedPhoto}` : resolvedPhoto
              })}
              className={`w-full shadow-xl ${selectPortraitSideBySide ? 'max-h-full' : ''}`}
              sessionId={id || ''}
            />
          </div>
        )}
      </div>

      {stackSelectTop && (
        <div className="flex shrink-0 flex-col items-center gap-2 px-2 pt-1">
          {captureMode === 'frame-based' && renderSharedSessionTimerBelowStrip()}
          {captureMode === 'standard' && selectionTimeoutSec > 0 && <CountdownDisplay />}
        </div>
      )}

      {!stackSelectTop && (
        <div
          className={`flex shrink-0 flex-col items-center ${selectPortraitSideBySide ? 'gap-2 pt-0' : 'gap-4 pt-4'}`}
        >
          {captureMode === 'standard' && <CountdownDisplay />}
          {/* Always the primary treatment: the ghost/outline incomplete state read
              as the weakest element on the page instead of "main action, disabled"
              (wave-1 audit). Button's disabled styling still communicates state. */}
          <Button
            text={buttonText}
            onClick={handleContinue}
            data-nav-to="result"
            disabled={selectedPhotos.length !== maxSelectablePhotos || isUploading || !isPreviewReady}
            variant="primary"
            className="!min-h-[64px] !px-10 !text-xl"
          />
        </div>
      )}
    </GlassPanel>
  )

  return (
    <BackgroundContainer className={`relative flex min-h-0 ${stackSelectTop ? 'h-full flex-col' : 'h-full'}`}>
      {captureMode === 'frame-based' && !stackSelectTop && renderSharedSessionTimer()}
      <ProcessingModal
        isVisible={isUploading || videoProcessingId !== null}
        title={
          videoProcessingId
            ? t('select.processingVideo', 'Processing Video...')
            : t('select.processing', 'Processing...')
        }
        message={t('select.pleaseWait', 'Please wait while we process your photos...')}
        primaryColor={primaryColor}
      />

      {stackSelectTop ? (
        <>
          {previewPanel}
          {photoSelectionBlock}
        </>
      ) : (
        <>
          {photoSelectionBlock}
          {previewPanel}
        </>
      )}
      {stackSelectTop && (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[100] flex justify-end p-0">
          <div className="pointer-events-auto pr-4 pb-12">
            <Button
              text={buttonText}
              onClick={handleContinue}
              data-nav-to="result"
              disabled={selectedPhotos.length !== maxSelectablePhotos || isUploading || !isPreviewReady}
              variant="primary"
              className={`${selectNextStackedBtn} shadow-lg`}
            />
          </div>
        </div>
      )}
    </BackgroundContainer>
  )
}

export default SelectPage
